import { useMaintenanceInfo } from "@/api/services/global/maintenance";
import { useInformationList } from "@/api/services/main/information";
import { FileItem } from "@/components/elements/FileItem";
import { InfiniteScroll } from "@/components/elements/InfiniteScroll";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { QuoteScope } from "@/components/elements/QuoteScope";
import { text } from "@/theme/colors";
import { Box, Stack, Typography } from "@mui/material";
import { sanitizeHTML } from "@/utils/pieces";
import React from "react";
import { dayjs } from "@/utils/dayjs";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";

// TopAlert component to display maintenance and important information alerts
export const TopAlert = () => {
  // Fetch information list with type "important"
  const {
    dataFormatter: informationListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInformationList({
    types: "important",
  });

  // Fetch maintenance information
  const rs = useMaintenanceInfo();
  const maintenanceInfo = rs?.data?.data;

  // Check if maintenance message should be displayed based on date and time in Tokyo timezone
  const shouldShowMaintenanceMessage = () => {
    if (!maintenanceInfo || !maintenanceInfo.modeStartDateTime || !maintenanceInfo.modeEndDateTime || !maintenanceInfo.maintenanceMessage) {
      return false;
    }

    // Use Tokyo timezone for all dates
    const now = dayjs().tz();
    const startDateTime = dayjs(maintenanceInfo.modeStartDateTime).tz();
    const endDateTime = dayjs(maintenanceInfo.modeEndDateTime).tz();

    // Calculate the effective start time as 09:00:00 of the start date
    const effectiveStartTime = startDateTime.startOf("day").add(9, "hour");

    // Check if current time is within the maintenance window
    return now.isAfter(effectiveStartTime) && now.isBefore(endDateTime);
  };

  // Format maintenance message with Tokyo timezone and handle newlines
  const formatMaintenanceMessage = (message: string, startDateTime: string, endDateTime: string) => {
    return message
      .replace(/%BEGIN_TIME%/g, dayjs(startDateTime).tz().format(DATE_TIME_FORMAT_WITH_SECONDS))
      .replace(/%END_TIME%/g, dayjs(endDateTime).tz().format(DATE_TIME_FORMAT_WITH_SECONDS))
      .replace(/\n/g, "<br />");
  };

  // Show loading area if data is not ready
  if (!informationListData || isLoading) return <LoadingArea height={50} />;

  return (
    // Infinite scroll for loading more information items
    <InfiniteScroll loadMore={fetchNextPage} hasMore={hasNextPage}>
      <Stack gap={1}>
        {shouldShowMaintenanceMessage() &&
          maintenanceInfo?.maintenanceMessage &&
          maintenanceInfo?.modeStartDateTime &&
          maintenanceInfo?.modeEndDateTime && (
            // Display maintenance notification if conditions are met
            <QuoteScope variant="danger">
              <Stack gap={1.25}>
                <Typography color={text.primary} variant="sub16Bold">
                  irohanaメンテナンスのお知らせ
                </Typography>
                <Typography variant="body14">
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(
                        formatMaintenanceMessage(
                          maintenanceInfo.maintenanceMessage,
                          maintenanceInfo.modeStartDateTime,
                          maintenanceInfo.modeEndDateTime,
                        ),
                      ),
                    }}
                  />
                </Typography>
              </Stack>
            </QuoteScope>
          )}
        {informationListData.map((item) => {
          // Render each information item
          return (
            <QuoteScope key={item.id} variant="danger">
              <Stack gap={1.25}>
                <Typography color={text.primary} variant="sub16Bold">
                  {item.title}
                </Typography>
                <Typography variant="body14">{item.content && <Box dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.content) }} />}</Typography>
                {item.filePath && <FileItem filePath={item.filePath} downloadable />}
              </Stack>
            </QuoteScope>
          );
        })}
      </Stack>
    </InfiniteScroll>
  );
};
